import { useIsSubscriptionEnabled } from "@/hooks/use-is-subscription-enabled";
import {
  getMonthlyTrialEligibility,
  type TrialEligibility,
} from "@/utils/revenue-cat/trial-eligibility";
import type { PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { useEffect, useRef, useState } from "react";

/**
 * Resolves whether the user is eligible for the free trial on the monthly
 * package, for driving paywall copy. Defaults to "unknown" while resolving (and
 * forever on web), which callers should treat optimistically as "show the trial".
 */
export const useMonthlyTrialEligibility = (
  monthlyPackage: PurchasesPackage | undefined,
): TrialEligibility => {
  const { isEnabled } = useIsSubscriptionEnabled();
  const [eligibility, setEligibility] = useState<TrialEligibility>("unknown");
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!monthlyPackage) {
      return;
    }

    let active = true;
    void getMonthlyTrialEligibility(monthlyPackage, isEnabled).then((result) => {
      if (active && isMountedRef.current) {
        setEligibility(result);
      }
    });

    return () => {
      active = false;
    };
  }, [isEnabled, monthlyPackage]);

  return eligibility;
};
